import { Link, RouteComponentProps } from '@reach/router';
import { LessonPlan, LessonPlanEquipment, LessonPlanResource, LessonsApi } from 'api/lessonsApi';
import DefaultImage from 'images/lessons/Img_Default.jpg';
import FullScreenIcon from 'images/lessons/Full_Screen_Icon.png';
import ResourceCardImage from 'images/lessons/Img_Resource_Document.png';
import LessonPageStatus from 'components/lessons/LessonPageStatus';
import { LessonPrintParts, useLessonPrint } from 'components/lessons/LessonPrint';
import {
  closedAccessButton,
  downloadFileInPlace,
  LessonsRouteParams,
  openFileInNewTab,
  splitLines,
  unitPath,
  useLessonsPage,
} from 'components/lessons/lessonsHelpers';
import LessonTop from 'components/lessons/LessonTop';
import Page from 'components/shared/Page';
import React, { useState } from 'react';

type Props = RouteComponentProps<LessonsRouteParams>;

const LessonPlanPage: React.FC<Props> = (props) => {
  const result = useLessonsPage<LessonPlan>(props, (query, lessonNumber) =>
    LessonsApi.getLessonPlan(query, lessonNumber)
  );
  const subjectsId = result && typeof result !== 'string' ? result.subject.subjectsID : null;
  const { print, popup, errorMessage } = useLessonPrint(subjectsId);
  const [bFileError, setBFileError] = useState(false);

  if (result === null || typeof result === 'string') {
    return <LessonPageStatus status={result} />;
  }

  const { subject, labels, query, details: lessonPlan } = result;
  const { lesson, activity, access } = lessonPlan;
  const path = unitPath(subject.internalCode, lessonPlan.keyStage.strippedName, activity.strippedName);
  const lessonPath = (lessonNumber: number) => `${path}/${lessonNumber}?years=${query.years}`;
  const wholeChildObjectives = splitLines(lesson.wholeChildObjectives);
  const bEYFS = lessonPlan.keyStageName.toLowerCase() === 'eyfs';
  const criteriaYears = Array.from(
    new Set(lessonPlan.successCriterias.map((item) => item.applicableYearsNames ?? ''))
  );

  const openFile = (getUrl: () => Promise<string | null>, bDownload = false) => {
    setBFileError(false);
    (bDownload ? downloadFileInPlace(getUrl) : openFileInNewTab(getUrl)).then((bOpened) =>
      setBFileError(!bOpened)
    );
  };

  const equipmentItem = (item: LessonPlanEquipment, bButtons: boolean) => (
    <div key={item.virtualPECupboardsId} className="LessonEquipmentItem">
      <img
        src={item.imagePath || (item.bIsResourceCard ? ResourceCardImage : DefaultImage)}
        alt={item.name}
      />
      <div className="LessonEquipmentText">
        <div className="LessonEquipmentName">{item.name}</div>
        {item.pupilsPerItem && <div>{`x ${item.pupilsPerItem}`}</div>}
        {bButtons && item.bIsResourceCard && (
          <div className="LessonEquipmentButtons LessonNoPrint">
            {[false, true].map((bDownload) => (
              <button
                key={String(bDownload)}
                type="button"
                className="Btn BtnOrange"
                onClick={() =>
                  openFile(
                    () =>
                      LessonsApi.getEquipmentFileUrl(
                        subject.subjectsID,
                        item.equipmentsId,
                        bDownload
                      ),
                    bDownload
                  )
                }
              >
                {bDownload ? 'Download' : 'Open'}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const resourceButtons = (resource: LessonPlanResource) => {
    const getUrl = (bDownload: boolean) => () =>
      LessonsApi.getResourceFileUrl(subject.subjectsID, resource.resourcesId, bDownload);

    if (!resource.bCanOpen && !resource.bCanDownload) {
      const button = access.bFullAccess
        ? { text: 'Subscribers Only', href: null, bDisabled: true }
        : closedAccessButton(access);
      return button.href ? (
        <Link className="Btn BtnOrange" to={button.href}>
          {button.text}
        </Link>
      ) : (
        <span className="Btn BtnGrey">{button.text}</span>
      );
    }

    return (
      <>
        {resource.bCanOpen && (
          <button type="button" className="Btn BtnOrange" onClick={() => openFile(getUrl(false))}>
            Open
          </button>
        )}
        {resource.bCanDownload && (
          <button
            type="button"
            className="Btn BtnOrange"
            onClick={() => openFile(getUrl(true), true)}
          >
            Download
          </button>
        )}
      </>
    );
  };

  const successCriteriaList = (yearNames: string | null) => (
    <ul>
      {lessonPlan.successCriterias
        .filter((item) => yearNames === null || (item.applicableYearsNames ?? '') === yearNames)
        .map((item) => (
          <li key={item.assessmentSuccessCriteriasId}>{item.criteria}</li>
        ))}
    </ul>
  );

  const planItems = [
    { heading: labels.WarmUpAndIntroduction, text: lesson.warmUpAndIntroduction },
    { heading: 'Develop and Apply', text: lesson.skillDevelopment },
    { heading: 'Creating and Performing the Dance', text: lesson.creatingPerformingTheDance },
    { heading: 'Reflect and Review', text: lesson.plenary },
  ].filter((item) => item.text && item.text.trim() !== '');

  return (
    <Page bypassPageAccess>
      <div className="LessonPage">
        <LessonPrintParts schoolName={lessonPlan.schoolName} />
        <LessonTop
          activity={activity}
          keyStageName={lessonPlan.keyStageName}
          backPath={`${path}?years=${query.years}`}
          subtitle={`Lesson ${lesson.lessonNumber}${lesson.theme ? ` - ${lesson.theme}` : ''}`}
          onPrint={print}
        >
          {errorMessage}
        </LessonTop>
        <div className="WidthContent LessonAdjacent LessonNoPrint">
          {lessonPlan.previousLesson && (
            <Link to={lessonPath(lessonPlan.previousLesson.lessonNumber)} className="Btn BtnOrange">
              Previous Lesson
            </Link>
          )}
          {lessonPlan.nextLesson && (
            <Link
              to={lessonPath(lessonPlan.nextLesson.lessonNumber)}
              className="Btn BtnOrange LessonNext"
            >
              Next Lesson
            </Link>
          )}
        </div>
        <div className="WidthContent LessonSection">
          <h2>Learning Objective</h2>
          {lesson.learningObjectives.map((item, index) => (
            <p key={index} className="LessonObjective">
              {item.objective}
            </p>
          ))}
        </div>
        {(lessonPlan.successCriterias.length > 0 || wholeChildObjectives.length > 0) && (
          <div className="WidthContent LessonSection LessonColumns">
            <div className="LessonColumn">
              <h2>{labels.SuccessCriteria}</h2>
              {bEYFS && wholeChildObjectives.length === 0
                ? successCriteriaList(null)
                : criteriaYears.map((yearNames) => (
                    <React.Fragment key={yearNames}>
                      {criteriaYears.length > 1 && (
                        <div className="LessonTableLabel">{yearNames}</div>
                      )}
                      {successCriteriaList(yearNames)}
                    </React.Fragment>
                  ))}
            </div>
            {wholeChildObjectives.length > 0 && (
              <div
                className="LessonColumn LessonWholeChild"
                style={
                  lesson.objectivesImagePath
                    ? { backgroundImage: `url(${lesson.objectivesImagePath})` }
                    : undefined
                }
              >
                <h2>Whole Child Objectives</h2>
                <ul>
                  {wholeChildObjectives.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="WidthContent LessonSection">
          <h2>{labels.Equipment}</h2>
          <div className="LessonEquipmentList">
            {lessonPlan.presentationResources.length > 0 && (
              <div className="LessonEquipmentItem">
                <img className="LessonPresentationIcon" src={FullScreenIcon} alt="" />
                <div className="LessonEquipmentText">
                  <div className="LessonEquipmentName">{lessonPlan.presentationName}</div>
                  <div className="LessonEquipmentButtons LessonNoPrint">
                    <a
                      className="Btn BtnOrange"
                      href={`${path}/${lesson.lessonNumber}/presentation?years=${query.years}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open
                    </a>
                  </div>
                </div>
              </div>
            )}
            {lessonPlan.compulsoryEquipment.map((item) => equipmentItem(item, true))}
            {lessonPlan.resources.map((resource) => (
              <div key={resource.resourcesId} className="LessonEquipmentItem">
                <img src={ResourceCardImage} alt="" />
                <div className="LessonEquipmentText">
                  <div className="LessonEquipmentName">{resource.name}</div>
                  <div>{resource.resourceType}</div>
                  <div className="LessonEquipmentButtons LessonNoPrint">
                    {resourceButtons(resource)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {bFileError && (
            <p className="ErrorLabel LessonNoPrint">An error occurred, please try again.</p>
          )}
          {lessonPlan.optionalEquipment.length > 0 && (
            <>
              <h3>Optional:</h3>
              <div className="LessonEquipmentList">
                {lessonPlan.optionalEquipment.map((item) => equipmentItem(item, false))}
              </div>
            </>
          )}
        </div>
        {planItems.map((item) => (
          <div key={item.heading} className="WidthContent LessonSection">
            <h2>{item.heading}</h2>
            <div
              className="LessonPlanText"
              dangerouslySetInnerHTML={{ __html: item.text as string }}
            />
          </div>
        ))}
      </div>
      {popup}
    </Page>
  );
};

export default LessonPlanPage;
